import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Station, UserProfile, SensorType } from "../../../types/domain";
import { queryKeys } from "../../../lib/queryKeys";
import { fetchCrops } from "../../crops/services/crops.service";
import { Crop } from "../../crops/types";
import { hasStationAccess } from "../../../utils/permissions";
import { SystemRole } from "../../../types/common";
import { useTableData } from "../../../hooks/useTableData";
import type { Database } from "../../../types/supabase";

type Tables = Database["public"]["Tables"];

export const useStations = (userProfile: UserProfile | null) => {
  const baseQuery = useTableData<Station>({
    table: "stations",
    orderBy: { column: "name", ascending: true },
    enabled: !!userProfile
  });

  // Memoizado: sin esto cada render devolvía un array nuevo y todos los
  // useMemo que dependen de las estaciones (orden, mapa, riesgos) se recalculaban.
  const stationsData = baseQuery.data;
  const filteredData: Station[] = useMemo(
    () =>
      userProfile?.role === SystemRole.SUPERADMIN
        ? stationsData
        : stationsData.filter((station) => hasStationAccess(userProfile, station.id)),
    [stationsData, userProfile]
  );

  return {
    ...baseQuery,
    data: filteredData
  };
};

export const useGroups = () => {
  return useTableData<Tables["groups"]["Row"]>({
    table: "groups",
    orderBy: { column: "name", ascending: true }
  });
};

export const useSensors = () => {
  return useTableData<Tables["sensors"]["Row"]>({
    table: "sensors",
    orderBy: { column: "name", ascending: true }
  });
};

export const useSensorTypes = () => {
  return useTableData<SensorType>({
    table: "sensor_types",
    orderBy: { column: "name", ascending: true }
  });
};

export const useMeasurementUnits = () => {
  return useTableData<Tables["measurement_units"]["Row"]>({
    table: "measurement_units",
    orderBy: { column: "symbol", ascending: true }
  });
};

export const useDevices = () => {
  return useTableData<Tables["devices"]["Row"]>({
    table: "devices",
    orderBy: { column: "id", ascending: true },
    // `last_seen_at`, reset diagnostics y config-shadow cambian con cada POST.
    // Sin sondeo, la salud del dashboard quedaba congelada al abrir la página.
    refetchInterval: 60 * 1000,
  });
};


export const useCrops = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<Crop[]>({
    enabled,
    queryKey: queryKeys.crops(),
    queryFn: async () => {
      const response = await fetchCrops();
      if (response.status === 'error') {
        throw new Error(response.error?.message || 'No se pudieron cargar los cultivos.');
      }
      return response.data || [];
    }
  });
};
